// src/services/apartadosService.js
//
// Gestión de apartados (reservas de 30 días).
//
// Un apartado es una intención de compra no comprometida: la propiedad
// queda en estado "apartada" / disponible:false durante 30 días.
// Si el cliente formaliza, se convierte en deuda (convertirApartado).
// Si vence o se cancela, la propiedad regresa a "disponible".
//
// Colección: apartados/{id}
// {
//   clienteId, clienteNombre,
//   propiedadId, propiedades[],  propiedadIds[],
//   concepto, notas,
//   fechaApartado:   Timestamp,
//   fechaVencimiento: Timestamp  (+30 días),
//   monto:  number              (señal/depósito — 0 si no aplica, por implementar),
//   estado: "activo" | "vencido" | "convertido" | "cancelado",
//   deudaId: string | null,     (cuando se convierte en deuda),
//   creadoPor: uid | null,
//   creado: serverTimestamp(),
//   updatedAt: serverTimestamp() | null,
// }

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../Firebase/firebaseconfig";
import { registrarAuditoria } from "../helpers/auditoria";
import { metodoPagoPayload } from "../helpers/metodosPago";
import { toJsDate, toLocalISODate, hoyLocalISO } from "../helpers/fechas";

const DIAS_APARTADO = 30;
const MAX_RENOVACIONES = 2;

// ── Helper: resolver contexto (proyecto/fracc) a partir de la primera propiedad
async function resolverContextoCaja(propiedadId) {
  if (!propiedadId) return null;
  try {
    const pSnap = await getDoc(doc(db, "propiedades", propiedadId));
    if (!pSnap.exists()) return null;
    const p = pSnap.data();
    if (!p.proyectoId) return null;
    return {
      proyectoId: p.proyectoId,
      proyectoNombre: p.proyectoNombre || "",
      fraccionamientoId: p.fraccionamientoId || "",
      fraccionamientoNombre: p.fraccNombre || p.fraccionamientoNombre || "",
      propiedadId,
      propiedadFolio: p.folio || p.codigoHumano || "",
    };
  } catch {
    return null;
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildFechaVencimiento(desde = new Date()) {
  const v = new Date(desde);
  v.setHours(0, 0, 0, 0);
  v.setDate(v.getDate() + DIAS_APARTADO);
  return Timestamp.fromDate(v);
}

function getCurrentUid() {
  return getAuth().currentUser?.uid ?? null;
}

// ── crearApartado ──────────────────────────────────────────────────────────────
/**
 * Crea un apartado y marca las propiedades como "apartada".
 *
 * El apartado guarda TODOS los datos de la venta proyectada y la comisión,
 * de modo que al convertir (apartado → deuda) el formulario se pre-llene
 * completamente y la operación de venta sea solo "confirmar".
 *
 * @param {object} params
 * @param {string}   params.clienteId
 * @param {string}   params.clienteNombre
 * @param {Array}    params.propiedades         propiedades seleccionadas (objetos completos)
 * @param {string}   [params.concepto]
 * @param {string}   [params.notas]
 * @param {object}   [params.ventaProyectada]   { monto, enganche, mensualidad, plazo, fechaInicio,
 *                                                referencia, fueraPaisRegularmente, responsableId }
 * @param {object}   [params.comisionProyectada] config completa de comisión (mismo shape que crearComision)
 * @param {object}   [params.senial]            { monto, metodo, fecha, referencia, notas }
 * @returns {Promise<{ id: string, cajaMovimientoSenialId: string|null }>}
 */
export async function crearApartado({
  clienteId,
  clienteNombre = "",
  propiedades = [],
  concepto = "",
  notas = "",
  ventaProyectada = null,
  comisionProyectada = null,
  senial = null,
}) {
  if (!clienteId) throw new Error("clienteId es obligatorio.");
  if (!propiedades.length) throw new Error("Selecciona al menos una propiedad.");

  const uid = getCurrentUid();
  const auth = getAuth();
  const user = auth.currentUser;

  const ahora = new Date();
  ahora.setHours(0, 0, 0, 0);
  const fechaApartado = Timestamp.fromDate(ahora);
  const fechaVencimiento = buildFechaVencimiento(ahora);

  const propPayload = propiedades.map((p) => ({
    propiedadId: p.id,
    folio: p.folio || "",
    codigo: p.codigoHumano || "",
    nombre: p.nombre || p.alias || `Mz ${p.manzana || "?"} Lt ${p.lote || "?"}`,
    manzana: p.manzana ?? "",
    lote: p.lote ?? "",
    fraccionamientoId: p.fraccionamientoId || "",
    proyectoId: p.proyectoId || "",
  }));

  const propPrincipal = propPayload[0];

  // Normalizar señal: si viene monto>0 la registramos; si no, queda en null.
  const senialMonto = Math.max(Number(senial?.monto || 0), 0);
  const senialPayload =
    senialMonto > 0
      ? {
          monto: senialMonto,
          ...metodoPagoPayload(senial?.metodo || "efectivo"),
          fecha: senial?.fecha
            ? Timestamp.fromDate(new Date(`${senial.fecha}T00:00:00`))
            : fechaApartado,
          referencia: senial?.referencia || "",
          notas: senial?.notas || "",
          estado: "retenida", // retenida → aplicada (al convertir) | reembolsada (al liberar)
          cajaMovimientoId: null, // se asigna en la transacción si hay contexto de caja
        }
      : null;

  // Resolver contexto de caja para el movimiento de la señal (fuera de tx)
  const ctxCaja = senialPayload
    ? await resolverContextoCaja(propPrincipal.propiedadId)
    : null;

  let apartadoId = "";
  let cajaMovimientoSenialId = null;

  await runTransaction(db, async (tx) => {
    // Verificar disponibilidad de cada propiedad
    for (const p of propiedades) {
      const pSnap = await tx.get(doc(db, "propiedades", p.id));
      if (!pSnap.exists()) throw new Error(`La propiedad (${p.folio || p.id}) no existe.`);
      const pdata = pSnap.data();
      if (pdata.disponible === false) {
        throw new Error(
          `La propiedad ${p.folio || p.id} no está disponible (${pdata.estado || "ocupada"}).`
        );
      }
    }

    // Crear doc apartado
    const aRef = doc(collection(db, "apartados"));
    apartadoId = aRef.id;

    // Movimiento de caja por la señal (solo si hay contexto + monto)
    if (senialPayload && ctxCaja) {
      const cajaRef = doc(collection(db, "cajas_movimientos"));
      cajaMovimientoSenialId = cajaRef.id;
      tx.set(cajaRef, {
        tipo: "entrada",
        monto: senialPayload.monto,
        concepto: `Señal de apartado — ${clienteNombre || clienteId}`,
        conceptoTipo: "Señal de apartado",
        categoria: "señal_apartado",
        fecha: toLocalISODate(toJsDate(senialPayload.fecha) || new Date()),
        fechaMs: (toJsDate(senialPayload.fecha) || new Date()).getTime(),
        cajaScope: "proyecto",
        cajaId: ctxCaja.proyectoId,
        clusterId: "",
        transferenciaId: "",
        transferenciaInterna: false,
        proyectoId: ctxCaja.proyectoId,
        proyectoNombre: ctxCaja.proyectoNombre,
        fraccionamientoId: ctxCaja.fraccionamientoId,
        fraccionamientoNombre: ctxCaja.fraccionamientoNombre,
        propiedadId: ctxCaja.propiedadId,
        propiedadFolio: ctxCaja.propiedadFolio,
        propiedadIds: propPayload.map((p) => p.propiedadId),
        cxpId: "",
        deudaId: "",
        apartadoId,
        origenTipo: "apartado_senial",
        origen: "apartados.senial",
        metodo: senialPayload.metodo,
        metodoEtiqueta: senialPayload.metodoEtiqueta || "",
        esTransferenciaBancaria: !!senialPayload.esTransferenciaBancaria,
        referencia: senialPayload.referencia,
        notas: senialPayload.notas,
        activo: true,
        cancelado: false,
        createdAt: serverTimestamp(),
        createdAtMs: Date.now(),
        createdByUid: uid || "",
        createdByEmail: user?.email || "",
      });
      senialPayload.cajaMovimientoId = cajaMovimientoSenialId;
    }

    tx.set(aRef, {
      clienteId,
      clienteNombre,
      propiedadId: propPrincipal.propiedadId,
      propiedades: propPayload,
      propiedadIds: propiedades.map((p) => p.id),
      concepto: concepto || "",
      notas: notas || "",
      fechaApartado,
      fechaVencimiento,
      // ── Señal ─────────────────────────────────────────────────────────
      monto: senialMonto, // alias mantenido por retrocompatibilidad
      senial: senialPayload,
      // ── Venta proyectada (pre-llenado al convertir) ───────────────────
      ventaProyectada: ventaProyectada || null,
      // ── Comisión proyectada ───────────────────────────────────────────
      comisionProyectada: comisionProyectada || null,
      // ── Estado / linkage ──────────────────────────────────────────────
      estado: "activo",
      deudaId: null,
      // ── Renovaciones ──────────────────────────────────────────────────
      renovaciones: 0,
      historialRenovaciones: [],
      // ── Meta ──────────────────────────────────────────────────────────
      creadoPor: uid,
      creado: serverTimestamp(),
      updatedAt: null,
    });

    // Marcar propiedades
    for (const p of propiedades) {
      tx.update(doc(db, "propiedades", p.id), {
        estado: "apartada",
        disponible: false,
        apartadoId: aRef.id,
        apartadoVence: fechaVencimiento,
        clienteId,
        actualizado: serverTimestamp(),
      });
    }
  });

  // Auditoría (no fatal)
  registrarAuditoria({
    accion: "apartados.crear",
    entidadTipo: "apartado",
    entidadId: apartadoId,
    detalle: `Cliente ${clienteId}`,
    despues: {
      clienteId,
      concepto,
      propiedadIds: propiedades.map((p) => p.id),
      fechaVencimiento: (toJsDate(fechaVencimiento) || new Date()).toISOString().split("T")[0],
      senialMonto,
      cajaMovimientoSenialId,
      ventaMonto: Number(ventaProyectada?.monto || 0),
      conComision: Boolean(comisionProyectada?.activa),
    },
  }).catch((e) => console.warn("Audit apartado:", e));

  return { id: apartadoId, cajaMovimientoSenialId };
}

// ── renovarApartado ───────────────────────────────────────────────────────────
/**
 * Extiende la fecha de vencimiento de un apartado activo.
 * Limitado a MAX_RENOVACIONES por apartado.
 *
 * @param {string} apartadoId
 * @param {number} [dias=30]
 * @returns {Promise<{ fechaVencimiento: Date, renovaciones: number }>}
 */
export async function renovarApartado(apartadoId, dias = 30) {
  if (!apartadoId) throw new Error("apartadoId es obligatorio.");

  const result = await runTransaction(db, async (tx) => {
    const aRef = doc(db, "apartados", apartadoId);

    // ── READS PRIMERO (regla de Firestore: reads antes de writes) ──────────
    const aSnap = await tx.get(aRef);
    if (!aSnap.exists()) throw new Error("Apartado no encontrado.");
    const a = aSnap.data();

    if (a.estado !== "activo") {
      throw new Error(`Solo se pueden renovar apartados activos (estado actual: ${a.estado}).`);
    }
    const yaRenovado = Number(a.renovaciones || 0);
    if (yaRenovado >= MAX_RENOVACIONES) {
      throw new Error(`Este apartado ya alcanzó el máximo de renovaciones (${MAX_RENOVACIONES}).`);
    }

    // Reads de propiedades en este mismo bloque (antes de cualquier write)
    const propIds = Array.isArray(a.propiedadIds)
      ? a.propiedadIds
      : [a.propiedadId].filter(Boolean);
    const propSnaps = [];
    for (const pId of propIds) {
      const pSnap = await tx.get(doc(db, "propiedades", pId));
      propSnaps.push({ id: pId, snap: pSnap });
    }

    // ── Cálculo de la nueva fecha ───────────────────────────────────────────
    const baseDate = toJsDate(a.fechaVencimiento) || new Date();
    const nuevaFecha = new Date(baseDate);
    nuevaFecha.setHours(0, 0, 0, 0);
    nuevaFecha.setDate(nuevaFecha.getDate() + dias);
    const nuevaTs = Timestamp.fromDate(nuevaFecha);

    const evento = {
      fecha: Timestamp.now(),
      dias,
      fechaAnterior: a.fechaVencimiento || null,
      fechaNueva: nuevaTs,
      uid: getCurrentUid() || "",
    };

    // ── WRITES (todos al final) ─────────────────────────────────────────────
    tx.update(aRef, {
      fechaVencimiento: nuevaTs,
      renovaciones: yaRenovado + 1,
      historialRenovaciones: [...(a.historialRenovaciones || []), evento],
      updatedAt: serverTimestamp(),
    });

    for (const item of propSnaps) {
      if (item.snap.exists() && item.snap.data().apartadoId === apartadoId) {
        tx.update(doc(db, "propiedades", item.id), {
          apartadoVence: nuevaTs,
          actualizado: serverTimestamp(),
        });
      }
    }

    return { fechaVencimiento: nuevaFecha, renovaciones: yaRenovado + 1 };
  });

  registrarAuditoria({
    accion: "apartados.renovar",
    entidadTipo: "apartado",
    entidadId: apartadoId,
    detalle: `+${dias} días — renovación #${result.renovaciones}`,
    despues: {
      fechaVencimiento: result.fechaVencimiento.toISOString().split("T")[0],
      renovaciones: result.renovaciones,
    },
  }).catch((e) => console.warn("Audit renovar apartado:", e));

  return result;
}

// ── liberarApartado ────────────────────────────────────────────────────────────
/**
 * Libera un apartado: regresa propiedades a "disponible" y cambia estado.
 *
 * @param {string}  apartadoId
 * @param {"vencido"|"cancelado"} motivo
 */
export async function liberarApartado(
  apartadoId,
  motivo = "cancelado",
  motivoCancelacion = "",
  opciones = {}
) {
  // opciones:
  //   reembolsarSenial: boolean — si la señal debe regresar al cliente
  //   metodoReembolso:  string — método del movimiento de salida (default: efectivo)
  const aSnap = await getDoc(doc(db, "apartados", apartadoId));
  if (!aSnap.exists()) throw new Error("Apartado no encontrado.");
  const apartado = { id: aSnap.id, ...aSnap.data() };

  if (apartado.estado === "convertido") {
    throw new Error("El apartado ya fue convertido a deuda.");
  }

  const senial = apartado.senial || null;
  const debeReembolsarSenial =
    !!opciones?.reembolsarSenial && senial && senial.estado === "retenida" && senial.monto > 0;

  // Resolver contexto de caja para el reembolso (fuera de tx)
  const ctxCaja = debeReembolsarSenial
    ? await resolverContextoCaja(apartado.propiedadId)
    : null;

  const auth = getAuth();
  const user = auth.currentUser;
  const uid = getCurrentUid();
  let cajaMovimientoReembolsoId = null;

  await runTransaction(db, async (tx) => {
    const propIds = Array.isArray(apartado.propiedadIds)
      ? apartado.propiedadIds
      : [apartado.propiedadId].filter(Boolean);

    // Reads primero
    const propSnaps = [];
    for (const pId of propIds) {
      const pSnap = await tx.get(doc(db, "propiedades", pId));
      propSnaps.push({ id: pId, snap: pSnap });
    }

    // Reembolso de señal (movimiento salida) — write
    if (debeReembolsarSenial && ctxCaja) {
      const cajaRef = doc(collection(db, "cajas_movimientos"));
      cajaMovimientoReembolsoId = cajaRef.id;
      const metodoReembolso = metodoPagoPayload(opciones.metodoReembolso || senial.metodo || "efectivo");
      tx.set(cajaRef, {
        tipo: "salida",
        monto: senial.monto,
        concepto: `Reembolso señal apartado — ${apartado.clienteNombre || apartado.clienteId}`,
        conceptoTipo: "Reembolso señal de apartado",
        categoria: "reembolso_senial",
        fecha: hoyLocalISO(),
        fechaMs: Date.now(),
        cajaScope: "proyecto",
        cajaId: ctxCaja.proyectoId,
        clusterId: "",
        transferenciaId: "",
        transferenciaInterna: false,
        proyectoId: ctxCaja.proyectoId,
        proyectoNombre: ctxCaja.proyectoNombre,
        fraccionamientoId: ctxCaja.fraccionamientoId,
        fraccionamientoNombre: ctxCaja.fraccionamientoNombre,
        propiedadId: ctxCaja.propiedadId,
        propiedadFolio: ctxCaja.propiedadFolio,
        propiedadIds: propIds,
        cxpId: "",
        deudaId: "",
        apartadoId,
        origenTipo: "apartado_reembolso_senial",
        origen: "apartados.reembolso",
        ...metodoReembolso,
        referencia: senial.referencia || "",
        notas: motivoCancelacion || "Reembolso de señal por cancelación de apartado",
        activo: true,
        cancelado: false,
        createdAt: serverTimestamp(),
        createdAtMs: Date.now(),
        createdByUid: uid || "",
        createdByEmail: user?.email || "",
      });
    }

    // Liberar propiedades
    for (const item of propSnaps) {
      if (!item.snap.exists()) continue;
      const pdata = item.snap.data();
      if (pdata.apartadoId === apartadoId) {
        tx.update(doc(db, "propiedades", item.id), {
          estado: "disponible",
          disponible: true,
          apartadoId: null,
          apartadoVence: null,
          clienteId: null,
          actualizado: serverTimestamp(),
        });
      }
    }

    // Actualizar apartado: estado + senial.estado si aplica
    const updateApartado = {
      estado: motivo,
      motivoCancelacion: motivoCancelacion || "",
      updatedAt: serverTimestamp(),
    };
    if (senial) {
      updateApartado.senial = {
        ...senial,
        estado: debeReembolsarSenial ? "reembolsada" : "retenida_final",
        cajaMovimientoReembolsoId: cajaMovimientoReembolsoId || null,
      };
    }
    tx.update(doc(db, "apartados", apartadoId), updateApartado);
  });

  // Auditoría
  registrarAuditoria({
    accion: motivo === "vencido" ? "apartados.vencer" : "apartados.cancelar",
    entidadTipo: "apartado",
    entidadId: apartadoId,
    detalle: motivoCancelacion || `Apartado ${motivo}`,
    despues: {
      estado: motivo,
      motivoCancelacion,
      senialReembolsada: debeReembolsarSenial,
      cajaMovimientoReembolsoId,
    },
  }).catch((e) => console.warn("Audit liberarApartado:", e));

  return { cajaMovimientoReembolsoId };
}

// ── listarApartadosPorCliente ─────────────────────────────────────────────────
export async function listarApartadosPorCliente(clienteId) {
  const q = query(
    collection(db, "apartados"),
    where("clienteId", "==", clienteId)
  );
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  items.sort((a, b) => {
    const ta = a.creado?.toMillis?.() ?? 0;
    const tb = b.creado?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return items;
}

// ── getApartado ───────────────────────────────────────────────────────────────
export async function getApartado(apartadoId) {
  const snap = await getDoc(doc(db, "apartados", apartadoId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

// ── listarApartados (admin) ───────────────────────────────────────────────────
/**
 * Lista apartados con filtros opcionales. Ordena client-side por fecha de creación.
 *
 * @param {object} [opts]
 * @param {string} [opts.estado]  — "activo" | "vencido" | "convertido" | "cancelado"
 * @returns {Promise<Array>}
 */
export async function listarApartados({ estado } = {}) {
  const constraints = [];
  if (estado) constraints.push(where("estado", "==", estado));
  const q = constraints.length
    ? query(collection(db, "apartados"), ...constraints)
    : query(collection(db, "apartados"));
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  items.sort((a, b) => {
    const ta = a.creado?.toMillis?.() ?? 0;
    const tb = b.creado?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return items;
}

// ── marcarApartadoComoConvertido ──────────────────────────────────────────────
/**
 * Marca un apartado como "convertido" enlazándolo a la deuda creada.
 * NO toca las propiedades — el flujo de alta de deuda ya las transiciona
 * de "apartada" → "vendida".
 *
 * @param {string} apartadoId
 * @param {string} deudaId
 */
export async function marcarApartadoComoConvertido(apartadoId, deudaId) {
  if (!apartadoId || !deudaId) throw new Error("apartadoId y deudaId son obligatorios.");

  await runTransaction(db, async (tx) => {
    const aSnap = await tx.get(doc(db, "apartados", apartadoId));
    if (!aSnap.exists()) throw new Error("Apartado no encontrado.");
    const apartado = aSnap.data();

    if (apartado.estado === "convertido") {
      throw new Error("El apartado ya está convertido.");
    }
    if (apartado.estado === "cancelado" || apartado.estado === "vencido") {
      throw new Error(`El apartado está en estado '${apartado.estado}' y no puede convertirse.`);
    }

    const update = {
      estado: "convertido",
      deudaId,
      updatedAt: serverTimestamp(),
    };
    // Si había señal retenida, marcarla como aplicada a la deuda
    if (apartado.senial && apartado.senial.estado === "retenida") {
      update.senial = {
        ...apartado.senial,
        estado: "aplicada",
        deudaIdAplicada: deudaId,
      };
    }
    tx.update(doc(db, "apartados", apartadoId), update);
  });

  // Auditoría
  registrarAuditoria({
    accion: "apartados.convertir",
    entidadTipo: "apartado",
    entidadId: apartadoId,
    detalle: `Apartado convertido a deuda ${deudaId}`,
    despues: { deudaId },
  }).catch((e) => console.warn("Audit convertirApartado:", e));
}

// ── marcarVencidos ─────────────────────────────────────────────────────────────
/**
 * Marca como vencidos todos los apartados activos cuya fechaVencimiento < hoy.
 * Útil para ejecutar al cargar vistas que listan apartados.
 * Fire-and-forget: no lanza errores al caller.
 */
export async function marcarVencidos() {
  try {
    const ahora = Timestamp.now();
    const q = query(
      collection(db, "apartados"),
      where("estado", "==", "activo"),
      where("fechaVencimiento", "<", ahora)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;

    for (const d of snap.docs) {
      const apartado = { id: d.id, ...d.data() };
      try {
        await liberarApartado(apartado.id, "vencido");
      } catch (e) {
        console.warn(`No se pudo liberar apartado ${apartado.id}:`, e);
      }
    }
  } catch (e) {
    console.warn("marcarVencidos error:", e);
  }
}
